import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { App } from '@galaxy/marketplace-apps';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AppPrice } from '@vendasta/marketplace-apps';
import { Cost, LineItem, Order, SalesOrdersService } from '@vendasta/sales-orders';
import { SalesOrderStoreService } from '@vendasta/sales-ui';

@Component({
  selector: 'orders-custom-price-form',
  templateUrl: './custom-price-form.component.html',
  styleUrls: ['./custom-price-form.component.scss'],
})
export class CustomPriceFormComponent implements OnInit {
  @Input() item: App;
  @Input() packageId: string;
  @Input() packageName: string;
  @Input() packageIconUrl: string;
  @Input() appPrice: AppPrice;
  @Input() order: Order;
  @Input() editable: boolean;
  @Input() partnerCurrency: string;
  formGroup: UntypedFormGroup;
  minValue: number;
  customValue: number;

  constructor(
    private salesOrdersService: SalesOrdersService,
    private snackbarService: SnackbarService,
    private readonly salesOrderStoreService: SalesOrderStoreService,
  ) {}

  ngOnInit(): void {
    this.minValue =
      this.appPrice.pricesForEditions?.['']?.pricesForCurrencies?.[
        this.partnerCurrency
      ]?.pricesForFrequencies?.[0]?.pricingRules?.[0]?.price;
    this.customValue = this.getLineItemValue(this.order.lineItems) || this.minValue;
  }

  // when a user inputs a value into the field handle it here
  changeSpendAmount(newSpendAmount: number): void {
    // purchase cost subtotal is considered the minimum allowed value for the budget-based (custom pricing, dynamic spend, whatever you want to call it) app
    if (newSpendAmount > this.minValue || (newSpendAmount === this.minValue && this.customValue > this.minValue)) {
      this.updateOrderItem(newSpendAmount);
      this.customValue = newSpendAmount;
    }
  }

  // updates the order's line items in the backend with the new value of this line item via API call
  private updateOrderItem(lineItemValue: number): void {
    if (!lineItemValue || !(lineItemValue > 0)) {
      return;
    }

    this.salesOrdersService
      .updateLineItems(this.order.orderId, this.order.businessId, this.getLineItemsWithUpdatedValue(lineItemValue))
      .subscribe((result: boolean) => {
        if (!result) {
          this.snackbarService.openErrorSnack('COMMON.ORDERS.ERROR_CUSTOM_PRICE');
        } else {
          this.salesOrderStoreService.refreshOrder(this.order.orderId, this.order.businessId);
          this.snackbarService.openSuccessSnack('COMMON.ORDERS.SUCCESS_CUSTOM_PRICE');
        }
      });
  }

  // Gets the custom price of this line item from the provided set of line items if it indeed occurs within the provided set, otherwise 0 is returned
  private getLineItemValue(lineItems: LineItem[]): number {
    if (!lineItems || lineItems.length === 0) {
      return 0;
    }

    for (const li of lineItems) {
      if (li.packageId && li.customPrices) {
        for (const customPriceMap of li.customPrices) {
          if (customPriceMap.productId === this.item?.key?.appId && li.packageId === this.packageId) {
            return customPriceMap.customPrice || 0;
          }
        }
      } else {
        if (li.appKey?.appId === this.item?.key?.appId && li.appKey?.editionId === this.item?.key?.editionId) {
          return li?.cost?.customPrice || 0;
        }
      }
    }
  }

  // Takes the existing line items on the input order and updates the corresponding price of this line item. Returns updated line items to be used in update API call.
  private getLineItemsWithUpdatedValue(newValue: number): LineItem[] {
    return this.order.lineItems.map((i: LineItem) => {
      if (this.packageId) {
        const customPriceMap = i?.customPrices?.find(
          (cpm) => cpm.productId === this.item?.key?.appId && i.packageId === this.packageId,
        );
        if (customPriceMap) {
          customPriceMap.customPrice = newValue;
        }
      } else {
        if (i.appKey?.appId === this.item?.key?.appId && i.appKey?.editionId === this.item?.key?.editionId) {
          i.cost = new Cost({ customPrice: newValue });
        }
      }
      return i;
    });
  }
}
